import React, { useState, useRef, useEffect } from "react"
import { chatCompletions, chatCompletionsStream, summarizeConversation } from "../../utils/ppAiLlmClient"
import { mcpInitializeAll, mcpToolsListAll, mcpToolsCall } from "../../utils/ppAiMcpClient"
import {
    isStandalone,
    LLM_FAST_TOOL_RESPONSE,
    LLM_STREAMING,
    PPAI_STANDALONE_AUTH_REQUIRED,
    PPAI_DEMO_LIMIT_REACHED,
    PPAI_DAILY_LIMIT_REACHED
} from "../../config/ppAiConfig"
import { clearStandaloneToken, getStandaloneToken } from "../../utils/ppAiStandaloneAuth"
import { useTranslation } from "react-i18next"
import { toast } from "react-toastify"
import piranhaIcon from "../../img/piranha.svg"
import preispiranhaLogo from "../../img/preispiranha_logo2.png"
import "../../styles/ppai.css"
import { CaptainShipIcon } from "./CaptainShipIcon"
import { DATABASE_PRICE_CSV_TOOL_NAME, buildMcpContextSystemMessage, buildOpenAITools } from "./mcpPrompt"
import { renderFormattedContent } from "./markdown"
import {
    STANDALONE_MAX_INPUT_LENGTH,
    getFriendlyErrorMessage,
    parsePlaceholderForGallery,
    extractCsvBlocks,
    looksLikePriceQuery,
    isNoRowsToolResult,
    getLastDatabasePriceText,
    hasOfferToolOutputs,
    isDemoLimitResult,
    userWantsCsv,
    getLastOfferMarkdown,
    buildToolMarkdown,
    DB_PRICE_TOOL_NAMES
} from "./toolHelpers"

function PPAI({ onStandaloneAuthRequired } = {}) {
    const { t } = useTranslation()
    const loadingMessageKeys = [
        "live_realtime_search",
        "maritime_1",
        "maritime_2",
        "maritime_3",
        "maritime_4",
        "maritime_5",
        "maritime_6",
        "maritime_7",
        "maritime_8",
        "searching_portals",
        "collecting_prices",
        "comparing_offers",
        "checking_delivery",
        "please_wait",
        "load_time"
    ]
    const loadingMessages = loadingMessageKeys.map((key) => t(`ppai.loading.${key}`))
    const [messages, setMessages] = useState([])
    const [input, setInput] = useState("")
    const [loading, setLoading] = useState(false)
    const [uploadedCsvFiles, setUploadedCsvFiles] = useState([])
    const [loadingMessageIndex, setLoadingMessageIndex] = useState(0)
    const [streamingAfterTools, setStreamingAfterTools] = useState(false)
    const [showStandaloneAuthButton, setShowStandaloneAuthButton] = useState(false)
    const [exampleGalleryPage, setExampleGalleryPage] = useState(0)
    const [showCsvInfo, setShowCsvInfo] = useState(false)
    const messagesEndRef = useRef(null)
    const fileInputRef = useRef(null)
    const inputRef = useRef(null)
    const downloadUrlsRef = useRef([])
    const abortControllerRef = useRef(null)
    const prevLoadingRef = useRef(false)

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" })
    }
    useEffect(() => {
        scrollToBottom()
    }, [messages])

    useEffect(() => {
        if (!loading) {
            setLoadingMessageIndex(0)
            return
        }
        const intervalId = setInterval(() => {
            setLoadingMessageIndex((prev) => (prev + 1) % loadingMessageKeys.length)
        }, 2500)
        return () => clearInterval(intervalId)
    }, [loading])

    useEffect(() => {
        if (prevLoadingRef.current && !loading) {
            inputRef.current?.focus()
        }
        prevLoadingRef.current = loading
    }, [loading])

    useEffect(() => {
        if (!isStandalone() || !showStandaloneAuthButton) return
        const checkToken = () => {
            if (getStandaloneToken()) setShowStandaloneAuthButton(false)
        }
        const id = setInterval(checkToken, 1000)
        checkToken()
        return () => clearInterval(id)
    }, [showStandaloneAuthButton])

    useEffect(() => {
        return () => {
            downloadUrlsRef.current.forEach((url) => URL.revokeObjectURL(url))
            downloadUrlsRef.current = []
        }
    }, [])

    const handleFileChange = async (event) => {
        const files = Array.from(event.target.files || [])
        if (files.length === 0) return
        const csvFiles = await Promise.all(
            files.map(async (file) => ({
                name: file.name,
                content: await file.text()
            }))
        )
        setUploadedCsvFiles(csvFiles)
        event.target.value = ""
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        if (isStandalone() && !getStandaloneToken()) {
            onStandaloneAuthRequired?.()
            return
        }
        const text = input.trim()
        if ((!text && uploadedCsvFiles.length === 0) || loading) return
        const fileAppendix = uploadedCsvFiles
            .map((f) => `\n\nCSV-Datei: ${f.name}\n\`\`\`csv\n${f.content}\n\`\`\``)
            .join("")
        const content = `${text}${fileAppendix}`.trim()
        if (isStandalone() && content.length > STANDALONE_MAX_INPUT_LENGTH) {
            toast.error(t("ppai.standalone_max_length"))
            return
        }
        setInput("")
        const userMessage = { role: "user", content }
        setMessages((prev) => [...prev, userMessage])
        setUploadedCsvFiles([])
        setLoading(true)
        abortControllerRef.current = new AbortController()
        const signal = abortControllerRef.current.signal
        try {
            let mcpTools = []
            try {
                await mcpInitializeAll({ signal })
                const listResult = await mcpToolsListAll({ signal })
                mcpTools = buildOpenAITools(listResult)
            } catch (mcpErr) {
                console.warn("MCP tools unavailable:", mcpErr)
                toast.warn(t("ppai.mcp_tools_unavailable"))
            }
            const hasOffersCsvTool = mcpTools.some((t) => t.function?.name === "offersMarkdownToCsv")
            const hasDatabasePricesCsvTool = mcpTools.some((t) => t.function?.name === DATABASE_PRICE_CSV_TOOL_NAME)
            if (userWantsCsv(text)) {
                const lastOffer = getLastOfferMarkdown(messages)
                const lastDbPrice = getLastDatabasePriceText(messages)
                const useDbForCsv =
                    hasDatabasePricesCsvTool &&
                    lastDbPrice?.text &&
                    (!lastOffer?.markdown || lastDbPrice.text.length >= lastOffer.markdown.length)
                const useOfferForCsv = hasOffersCsvTool && lastOffer?.markdown && !useDbForCsv
                if (useDbForCsv) {
                    try {
                        const csvResult = await mcpToolsCall(
                            DATABASE_PRICE_CSV_TOOL_NAME,
                            { text: lastDbPrice.text },
                            { signal }
                        )
                        const csvContent =
                            typeof csvResult === "string"
                                ? csvResult
                                : csvResult?.content?.[0]?.text ?? csvResult?.path ?? JSON.stringify(csvResult, null, 2)
                        const pathClean =
                            typeof csvContent === "string" ? csvContent.trim().replace(/^["']|["']$/g, "") : ""
                        const displayContent = pathClean.startsWith("/exporte/")
                            ? t("ppai.csv_created_download") + " `" + pathClean + "`"
                            : csvContent
                        setMessages((prev) => [
                            ...prev,
                            {
                                role: "assistant",
                                content: displayContent,
                                toolOutputs: [
                                    {
                                        name: DATABASE_PRICE_CSV_TOOL_NAME,
                                        args: { text: "(aus letzter Datenbank-Antwort)" },
                                        result: csvResult
                                    }
                                ]
                            }
                        ])
                        setLoading(false)
                        return
                    } catch (csvErr) {
                        console.warn(DATABASE_PRICE_CSV_TOOL_NAME + " failed, falling back to LLM:", csvErr)
                        toast.warn(t("ppai.csv_tool_failed"))
                    }
                }
                if (useOfferForCsv) {
                    try {
                        const csvResult = await mcpToolsCall(
                            "offersMarkdownToCsv",
                            { markdown: lastOffer.markdown },
                            { signal }
                        )
                        const csvContent =
                            typeof csvResult === "string"
                                ? csvResult
                                : csvResult?.content?.[0]?.text ?? csvResult?.path ?? JSON.stringify(csvResult, null, 2)
                        const pathClean =
                            typeof csvContent === "string" ? csvContent.trim().replace(/^["']|["']$/g, "") : ""
                        const displayContent = pathClean.startsWith("/exporte/")
                            ? t("ppai.csv_created_download") + " `" + pathClean + "`"
                            : csvContent
                        setMessages((prev) => [
                            ...prev,
                            {
                                role: "assistant",
                                content: displayContent,
                                toolOutputs: [
                                    {
                                        name: "offersMarkdownToCsv",
                                        args: { markdown: "(aus letzter Anfrage)" },
                                        result: csvResult
                                    }
                                ]
                            }
                        ])
                        setLoading(false)
                        return
                    } catch (csvErr) {
                        console.warn("offersMarkdownToCsv failed, falling back to LLM:", csvErr)
                        toast.warn(t("ppai.csv_tool_failed"))
                    }
                }
                if ((hasOffersCsvTool || hasDatabasePricesCsvTool) && !lastOffer?.markdown && !lastDbPrice?.text) {
                    setMessages((prev) => [...prev, { role: "assistant", content: t("ppai.csv_need_price_first") }])
                    setLoading(false)
                    return
                }
            }
            const allChatMessages = [...messages, userMessage]
            const CONTEXT_KEEP_RECENT = 4
            const CONTEXT_SUMMARY_THRESHOLD = 6
            let apiMessages
            let systemContent = buildMcpContextSystemMessage(mcpTools)
            if (allChatMessages.length <= CONTEXT_SUMMARY_THRESHOLD) {
                apiMessages = allChatMessages.map((m) => ({ role: m.role, content: m.content }))
            } else {
                const toSummarize = allChatMessages.slice(0, -CONTEXT_KEEP_RECENT)
                const recent = allChatMessages.slice(-CONTEXT_KEEP_RECENT)
                try {
                    const summary = await summarizeConversation(toSummarize, t)
                    if (summary) {
                        systemContent += "\n\nZusammenfassung der bisherigen Konversation:\n" + summary
                    }
                } catch (sumErr) {
                    console.warn("Kontext-Zusammenfassung fehlgeschlagen, sende vollständigen Verlauf:", sumErr)
                }
                apiMessages = recent.map((m) => ({ role: m.role, content: m.content }))
            }
            const systemMessage = { role: "system", content: systemContent }
            let currentMessages = [systemMessage, ...apiMessages]
            let maxRounds = 10
            let lastContent = ""
            let streamed = false
            const toolOutputs = []
            while (maxRounds-- > 0) {
                const options = mcpTools.length ? { tools: mcpTools, tool_choice: "auto", signal } : { signal }
                const response = await chatCompletions(currentMessages, options)
                const choice = response.choices?.[0]
                if (!choice) throw new Error("Keine Antwort vom Modell")
                const delta = choice.message
                if (delta.tool_calls?.length) {
                    currentMessages = [...currentMessages, delta]
                    for (const tc of delta.tool_calls) {
                        const name = tc.function?.name
                        const args = (() => {
                            try {
                                return JSON.parse(tc.function?.arguments || "{}")
                            } catch {
                                return {}
                            }
                        })()
                        const result = await mcpToolsCall(name, args, { signal })
                        if (isStandalone() && isDemoLimitResult(result)) {
                            clearStandaloneToken()
                            const e = new Error(PPAI_DEMO_LIMIT_REACHED)
                            e.code = PPAI_DEMO_LIMIT_REACHED
                            throw e
                        }
                        toolOutputs.push({
                            name,
                            args,
                            result
                        })
                        currentMessages = [
                            ...currentMessages,
                            {
                                role: "tool",
                                tool_call_id: tc.id,
                                content: typeof result === "string" ? result : JSON.stringify(result)
                            }
                        ]
                    }
                    const thisRoundOutputs = toolOutputs.slice(-delta.tool_calls.length)
                    const allDbNoRows =
                        thisRoundOutputs.length > 0 &&
                        thisRoundOutputs.every(
                            (o) => DB_PRICE_TOOL_NAMES.includes(o?.name) && isNoRowsToolResult(o?.result)
                        )
                    const hasGetPriceToolAvailable = mcpTools.some((t) => t.function?.name === "getPrice")
                    const hasLivePriceTools = mcpTools.some(
                        (t) => t.function?.name === "getPrice" || t.function?.name === "getPriceByGTINs"
                    )
                    const onlyWebSearchThisRound =
                        thisRoundOutputs.length > 0 && thisRoundOutputs.every((o) => o?.name === "webSearch")
                    if (onlyWebSearchThisRound && hasGetPriceToolAvailable && looksLikePriceQuery(text)) {
                        currentMessages = [
                            ...currentMessages,
                            {
                                role: "user",
                                content:
                                    "Für diese Preisanfrage nutze getPrice mit searchString = Produktbeschreibung des Nutzers (nicht webSearch). Rufe getPrice jetzt auf und gib die Live-Preisergebnisse aus."
                            }
                        ]
                        continue
                    }
                    if (allDbNoRows && hasLivePriceTools) {
                        currentMessages = [
                            ...currentMessages,
                            {
                                role: "user",
                                content:
                                    "Die Datenbank hat keine Treffer geliefert. Rufe jetzt getPrice auf (searchString = Produktbeschreibung/Suchanfrage des Nutzers) oder getPriceByGTINs bei bekannter GTIN, um den Preis live zu ermitteln. Antworte mit den Live-Preisergebnissen."
                            }
                        ]
                        continue
                    }
                    if (LLM_FAST_TOOL_RESPONSE) {
                        lastContent = buildToolMarkdown(toolOutputs)
                        break
                    }
                    if (LLM_STREAMING) {
                        setStreamingAfterTools(true)
                        const streamingMessage = { role: "assistant", content: "", isStreaming: true }
                        setMessages((prev) => [...prev, streamingMessage])
                        streamed = true
                        const streamResult = await chatCompletionsStream(
                            currentMessages,
                            { ...options, signal },
                            (partial) => {
                                setMessages((prev) => {
                                    const next = [...prev]
                                    const lastIndex = next.length - 1
                                    if (lastIndex >= 0 && next[lastIndex].role === "assistant") {
                                        next[lastIndex] = { ...next[lastIndex], content: partial, isStreaming: true }
                                    }
                                    return next
                                })
                            }
                        )
                        lastContent = streamResult?.text || ""
                        if (lastContent && !streamResult?.didEmit) {
                            await new Promise((resolve) => {
                                let index = 0
                                const step = 6
                                const intervalId = setInterval(() => {
                                    index = Math.min(index + step, lastContent.length)
                                    setMessages((prev) => {
                                        const next = [...prev]
                                        const lastIndex = next.length - 1
                                        if (lastIndex >= 0 && next[lastIndex].role === "assistant") {
                                            next[lastIndex] = {
                                                ...next[lastIndex],
                                                content: lastContent.slice(0, index),
                                                isStreaming: true
                                            }
                                        }
                                        return next
                                    })
                                    if (index >= lastContent.length) {
                                        clearInterval(intervalId)
                                        resolve()
                                    }
                                }, 30)
                            })
                        }
                        setMessages((prev) => {
                            const next = [...prev]
                            const lastIndex = next.length - 1
                            if (lastIndex >= 0 && next[lastIndex].role === "assistant") {
                                next[lastIndex] = { ...next[lastIndex], content: lastContent, isStreaming: false }
                            }
                            return next
                        })
                        break
                    }
                    continue
                }
                lastContent = delta.content || ""
                break
            }
            const csvBlocks = extractCsvBlocks(lastContent)
            const csvFiles = csvBlocks.map((csv, index) => {
                const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" })
                const url = URL.createObjectURL(blob)
                downloadUrlsRef.current.push(url)
                return { name: `llm_export_${index + 1}.csv`, url }
            })
            if (!streamed) {
                const textToShow = lastContent || t("ppai.no_response")
                const pendingToolOutputs = toolOutputs.length ? toolOutputs : undefined
                setMessages((prev) => [...prev, { role: "assistant", content: "", isStreaming: true, toolOutputs: pendingToolOutputs }])
                await new Promise((resolve) => {
                    let index = 0
                    const step = 6
                    const intervalId = setInterval(() => {
                        if (signal?.aborted) {
                            clearInterval(intervalId)
                            resolve()
                            return
                        }
                        index = Math.min(index + step, textToShow.length)
                        setMessages((prev) => {
                            const next = [...prev]
                            const lastIndex = next.length - 1
                            if (lastIndex >= 0 && next[lastIndex].role === "assistant") {
                                next[lastIndex] = {
                                    ...next[lastIndex],
                                    content: textToShow.slice(0, index),
                                    isStreaming: true
                                }
                            }
                            return next
                        })
                        if (index >= textToShow.length) {
                            clearInterval(intervalId)
                            resolve()
                        }
                    }, 30)
                })
                setMessages((prev) => {
                    const next = [...prev]
                    const lastIndex = next.length - 1
                    if (lastIndex >= 0 && next[lastIndex].role === "assistant") {
                        next[lastIndex] = {
                            ...next[lastIndex],
                            content: textToShow,
                            isStreaming: false,
                            csvFiles: csvFiles.length ? csvFiles : undefined,
                            toolOutputs: toolOutputs.length ? toolOutputs : undefined
                        }
                    }
                    return next
                })
            } else if (csvFiles.length > 0 || toolOutputs.length > 0) {
                setMessages((prev) => {
                    const next = [...prev]
                    const lastIndex = next.length - 1
                    if (lastIndex >= 0 && next[lastIndex].role === "assistant") {
                        next[lastIndex] = {
                            ...next[lastIndex],
                            csvFiles: csvFiles.length ? csvFiles : undefined,
                            toolOutputs: toolOutputs.length ? toolOutputs : undefined
                        }
                    }
                    return next
                })
            }
        } catch (err) {
            if (err.name === "AbortError") {
                setMessages((prev) => {
                    const next = [...prev]
                    const last = next[next.length - 1]
                    if (last?.role === "assistant" && last?.isStreaming && !last?.content?.trim()) {
                        next.pop()
                    }
                    return next
                })
                return
            }
            if (err?.code === PPAI_STANDALONE_AUTH_REQUIRED || err?.message === PPAI_STANDALONE_AUTH_REQUIRED) {
                if (isStandalone()) {
                    setShowStandaloneAuthButton(true)
                    setMessages((prev) => [
                        ...prev,
                        { role: "assistant", content: t("ppai.session_expired"), isError: true }
                    ])
                } else {
                    onStandaloneAuthRequired?.()
                }
                return
            }
            if (err?.code === PPAI_DAILY_LIMIT_REACHED || err?.message === PPAI_DAILY_LIMIT_REACHED) {
                setMessages((prev) => [
                    ...prev,
                    { role: "assistant", content: t("ppai.daily_limit_reached"), isError: true }
                ])
                return
            }
            if (err?.code === PPAI_DEMO_LIMIT_REACHED || err?.message === PPAI_DEMO_LIMIT_REACHED) {
                if (isStandalone()) setShowStandaloneAuthButton(true)
                setMessages((prev) => [
                    ...prev,
                    { role: "assistant", content: t("ppai.demo_limit_reached"), isError: true }
                ])
                return
            }
            const friendly = getFriendlyErrorMessage(err, t)
            setMessages((prev) => [
                ...prev,
                { role: "assistant", content: friendly || t("ppai.error"), isError: true }
            ])
        } finally {
            setStreamingAfterTools(false)
            setLoading(false)
        }
    }

    return (
        <>
        <div className="row">
            <div className="col-12">
                <div
                    className={`card card-outline ${isStandalone() ? "pp-ai-standalone-card" : "card-primary"}`}
                >
                    <div
                        className={`card-header pp-ai-header-piranha ${loading ? " pp-ai-header-piranha-loading" : ""}`}
                    >
                        {loading && (
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-danger ms-1 me-1"
                                onClick={() => abortControllerRef.current?.abort()}
                            >
                                {t("ppai.cancel")}
                            </button>
                        )}
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary pp-ai-reset-btn ms-2"
                            onClick={() => setMessages([])}
                            disabled={loading || messages.length === 0}
                            title={t("ppai.reset_chat")}
                        >
                            <i className="fas fa-sync-alt me-1" aria-hidden="true" />
                            <span className="pp-ai-header-btn-label">{t("ppai.reset_chat")}</span>
                        </button>
                        {isStandalone() && (showStandaloneAuthButton || !getStandaloneToken()) && (
                            <button
                                type="button"
                                className="btn btn-sm btn-primary ms-2"
                                onClick={() => onStandaloneAuthRequired?.()}
                                title={t("ppai.open_auth_button")}
                            >
                                <img src={piranhaIcon} alt="" className="pp-ai-auth-btn-icon me-1" />
                                <span className="pp-ai-header-btn-label">{t("ppai.open_auth_button")}</span>
                            </button>
                        )}
                    </div>
                    <div className="card-body p-0 d-flex flex-column pp-ai-card-body">
                        <div className="flex-grow-1 overflow-auto p-3 pp-ai-chat-scroll">
                            {messages.length === 0 && (() => {
                                const placeholderText = t(isStandalone() ? "ppai.placeholder_standalone" : "ppai.placeholder")
                                const { questions, footer } = parsePlaceholderForGallery(placeholderText)
                                const QUESTIONS_PER_PAGE = 6
                                const totalPages = Math.max(1, Math.ceil(questions.length / QUESTIONS_PER_PAGE))
                                const page = Math.min(exampleGalleryPage, totalPages - 1)
                                const pageQuestions = questions.slice(page * QUESTIONS_PER_PAGE, page * QUESTIONS_PER_PAGE + QUESTIONS_PER_PAGE)
                                return (
                                    <div className="pp-ai-example-gallery">
                                        {isStandalone() && (
                                            <div className="pp-ai-standalone-start-logo">
                                                <img src={preispiranhaLogo} alt="Preispiranha" />
                                                <span className="pp-ai-standalone-captain-title">{t("ppai.assistant_name")}</span>
                                            </div>
                                        )}
                                        {totalPages > 1 && (
                                            <div className="pp-ai-example-gallery-nav">
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-outline-secondary pp-ai-example-gallery-prev"
                                                    onClick={() => setExampleGalleryPage((p) => Math.max(0, p - 1))}
                                                    disabled={page <= 0}
                                                    aria-label={t("ppai.example_gallery_prev")}
                                                >
                                                    <i className="fas fa-chevron-left" />
                                                </button>
                                                <span className="pp-ai-example-gallery-dots">
                                                    {Array.from({ length: totalPages }, (_, i) => (
                                                        <button
                                                            key={i}
                                                            type="button"
                                                            className={`pp-ai-example-gallery-dot ${i === page ? "active" : ""}`}
                                                            onClick={() => setExampleGalleryPage(i)}
                                                            aria-label={`${i + 1} / ${totalPages}`}
                                                        />
                                                    ))}
                                                </span>
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-outline-secondary pp-ai-example-gallery-next"
                                                    onClick={() => setExampleGalleryPage((p) => Math.min(totalPages - 1, p + 1))}
                                                    disabled={page >= totalPages - 1}
                                                    aria-label={t("ppai.example_gallery_next")}
                                                >
                                                    <i className="fas fa-chevron-right" />
                                                </button>
                                            </div>
                                        )}
                                        <div className="pp-ai-example-gallery-buttons">
                                            {pageQuestions.map((q, idx) => (
                                                <button
                                                    key={`${page}-${idx}`}
                                                    type="button"
                                                    className="pp-ai-example-btn"
                                                    onClick={(e) => {
                                                        e.preventDefault()
                                                        e.stopPropagation()
                                                        setInput(q)
                                                        inputRef.current?.focus()
                                                    }}
                                                >
                                                    {q}
                                                </button>
                                            ))}
                                        </div>
                                        {footer && (
                                            <p className="pp-ai-placeholder-footer text-muted mb-0 mt-3">
                                                {footer}
                                            </p>
                                        )}
                                    </div>
                                )
                            })()}
                            {messages.map((msg, i) => (
                                <div key={i} className={`mb-3 ${msg.role === "user" ? "text-end" : "text-start"}`}>
                                    <span
                                        className={`badge ${
                                            msg.role === "user" ? "badge-primary" : "badge-secondary"
                                        } mb-2`}
                                    >
                                        {msg.role === "user" ? t("ppai.you") : t("ppai.assistant_name")}
                                    </span>
                                    <div
                                        className={`d-inline-block p-2 rounded pp-ai-msg-bubble ${
                                            msg.role === "user" ? "bg-primary text-white pp-ai-user-text" : "bg-light"
                                        } ${msg.isError ? "pp-ai-msg-error-wrap" : ""}`}
                                        style={{ maxWidth: "85%", whiteSpace: "pre-wrap" }}
                                    >
                                        {msg.isStreaming ? (
                                            <span>
                                                {renderFormattedContent(
                                                    msg.content,
                                                    t("ppai.offer_link_text"),
                                                    hasOfferToolOutputs(msg.toolOutputs)
                                                )}
                                                <span className="pp-ai-caret" />
                                            </span>
                                        ) : msg.isError ? (
                                            <div className="pp-ai-msg-error">
                                                {renderFormattedContent(
                                                    msg.content,
                                                    t("ppai.offer_link_text"),
                                                    false
                                                )}
                                            </div>
                                        ) : (
                                            renderFormattedContent(
                                                msg.content,
                                                t("ppai.offer_link_text"),
                                                hasOfferToolOutputs(msg.toolOutputs)
                                            )
                                        )}
                                        {!msg.isStreaming &&
                                            msg.toolOutputs?.length > 0 &&
                                            buildToolMarkdown(msg.toolOutputs) && (
                                                <details className="pp-ai-offers mt-2" open={false}>
                                                    <summary className="pp-ai-offers-title">
                                                        {hasOfferToolOutputs(msg.toolOutputs)
                                                            ? "Alle Angebote"
                                                            : "Vollständige Ausgabe"}
                                                    </summary>
                                                    {renderFormattedContent(
                                                        buildToolMarkdown(msg.toolOutputs),
                                                        t("ppai.offer_link_text"),
                                                        hasOfferToolOutputs(msg.toolOutputs)
                                                    )}
                                                    {hasOfferToolOutputs(msg.toolOutputs) && (
                                                        <div className="pp-ai-offers-note">{t("ppai.offers_note")}</div>
                                                    )}
                                                </details>
                                            )}
                                        {!isStandalone() &&
                                            msg.role === "assistant" &&
                                            msg.toolOutputs?.length > 0 && (
                                                <details className="pp-ai-raw-toggle mt-2">
                                                    <summary>MCP RAW</summary>
                                                    {msg.toolOutputs.map((tool, idx) => (
                                                        <div key={`tool-${idx}`} className="pp-ai-raw-block">
                                                            <div className="pp-ai-raw-title">{tool.name}</div>
                                                            <pre className="pp-ai-code">
                                                                {typeof tool.result === "string"
                                                                    ? tool.result
                                                                    : JSON.stringify(tool.result, null, 2)}
                                                            </pre>
                                                        </div>
                                                    ))}
                                                </details>
                                            )}
                                        {msg.role === "assistant" && msg.csvFiles?.length > 0 && (
                                            <div className="pp-ai-downloads">
                                                {msg.csvFiles.map((file) => (
                                                    <a
                                                        key={file.url}
                                                        href={file.url}
                                                        download={file.name}
                                                        className="pp-ai-download-link"
                                                    >
                                                        {file.name}
                                                    </a>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                            {loading && (
                                <div className="text-start mb-3">
                                    <span className="badge badge-secondary mb-1">{t("ppai.assistant_name")}</span>
                                    <div className="pp-ai-loading-banner">
                                        <div className="pp-ai-loading">
                                            <div className="pp-ai-loading-piranha">
                                                <CaptainShipIcon className="pp-ai-loading-ship" alt={t("ppai.assistant_name")} />
                                            </div>
                                            <div className="pp-ai-loading-dots">
                                                <span />
                                                <span />
                                                <span />
                                            </div>
                                            <div className="pp-ai-loading-text">
                                                {streamingAfterTools ? (
                                                    <span className="pp-ai-loading-line is-active">
                                                        {t("ppai.loading.processing_response")}
                                                    </span>
                                                ) : (
                                                    loadingMessages.map((msg, idx) => (
                                                        <span
                                                            key={loadingMessageKeys[idx]}
                                                            className={`pp-ai-loading-line ${
                                                                loadingMessageIndex === idx ? "is-active" : ""
                                                            }`}
                                                        >
                                                            {msg}
                                                        </span>
                                                    ))
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                            <div ref={messagesEndRef} />
                        </div>
                        <form onSubmit={handleSubmit} className="pp-ai-input-form border-top p-3 bg-light">
                            <div className="pp-ai-input-row">
                                <button
                                    type="button"
                                    className="pp-ai-upload-btn"
                                    onClick={() => setShowCsvInfo(true)}
                                    title="CSV hochladen"
                                >
                                    <i className="fas fa-file-csv" />
                                    <span>CSV</span>
                                </button>
                                <input
                                    type="file"
                                    ref={fileInputRef}
                                    accept=".csv,text/csv"
                                    multiple
                                    onChange={handleFileChange}
                                    hidden
                                    style={{ display: "none" }}
                                />
                                <textarea
                                    ref={inputRef}
                                    className="pp-ai-input form-control"
                                    rows="2"
                                    placeholder=""
                                    value={input}
                                    onChange={(e) =>
                                        setInput(
                                            isStandalone()
                                                ? e.target.value.slice(0, STANDALONE_MAX_INPUT_LENGTH)
                                                : e.target.value
                                        )
                                    }
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter" && !e.shiftKey) {
                                            e.preventDefault()
                                            handleSubmit(e)
                                        }
                                    }}
                                    disabled={loading}
                                    {...(isStandalone() ? { maxLength: STANDALONE_MAX_INPUT_LENGTH } : {})}
                                />
                                <button
                                    type="submit"
                                    className="pp-ai-send-btn"
                                    disabled={loading || (!input.trim() && uploadedCsvFiles.length === 0)}
                                    title={t("ppai.send")}
                                >
                                    <i className="fas fa-paper-plane" />
                                    <span className="pp-ai-send-btn-text">{t("ppai.send")}</span>
                                </button>
                            </div>
                            <p className="pp-ai-input-disclaimer mb-0">{t("ppai.input_disclaimer")}</p>
                            {uploadedCsvFiles.length > 0 && (
                                <div className="pp-ai-upload-list">
                                    {uploadedCsvFiles.map((f) => (
                                        <span key={f.name} className="pp-ai-upload-chip">
                                            {f.name}
                                        </span>
                                    ))}
                                </div>
                            )}
                        </form>
                    </div>
                </div>
            </div>
        </div>
        {showCsvInfo && (
            <div className="pp-ai-csv-modal-backdrop">
                <button
                    type="button"
                    onClick={() => setShowCsvInfo(false)}
                    aria-label={t("ppai.csv_upload.cancel")}
                    style={{ position: "absolute", inset: 0, border: "none", background: "transparent", padding: 0 }}
                />
                <div className="pp-ai-standalone-auth-modal" style={{ position: "relative", zIndex: 1 }}>
                    <div className="pp-ai-standalone-auth-modal-header">
                        <h5 className="mb-0">{t("ppai.csv_upload.title")}</h5>
                        <button
                            type="button"
                            className="pp-ai-standalone-auth-modal-close"
                            onClick={() => setShowCsvInfo(false)}
                            aria-label={t("ppai.csv_upload.cancel")}
                        >
                            <i className="fas fa-times" />
                        </button>
                    </div>
                    <div className="pp-ai-standalone-auth-modal-body">
                        <p className="text-muted small mb-3">
                            {t("ppai.csv_upload.description")}
                        </p>
                        <p className="text-muted small mb-4">
                            <strong>Hinweis:</strong> {t("ppai.csv_upload.hint")}
                        </p>
                        <button
                            type="button"
                            className="btn btn-primary w-100"
                            onClick={() => {
                                setShowCsvInfo(false)
                                fileInputRef.current?.click()
                            }}
                        >
                            {t("ppai.csv_upload.select_file")}
                        </button>
                        <button
                            type="button"
                            className="btn btn-outline-secondary w-100 btn-sm mt-2"
                            onClick={() => setShowCsvInfo(false)}
                        >
                            {t("ppai.csv_upload.cancel")}
                        </button>
                    </div>
                </div>
            </div>
        )}
        </>
    )
}

export default PPAI
